import React, { useState } from 'react';
import { Box, Text, Heading, Image, Input, Textarea, Button, FormControl, Icon, Flex } from '@chakra-ui/react';
import { FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import { useContent } from '../context/ContentContext';

export const EditableText = ({ section, contentKey, as = Text, ...props }) => {
  const { content, isEditMode, updateContent } = useContent();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  
  const textContent = content[section]?.[contentKey] || props.children || '';
  
  const handleEdit = () => {
    setValue(textContent);
    setIsEditing(true);
  };
  
  const handleSave = async () => {
    const result = await updateContent(section, contentKey, value);
    if (result.success) {
      setIsEditing(false);
    }
  };
  
  const handleCancel = () => {
    setIsEditing(false);
  };
  
  if (!isEditMode) {
    return as === Heading ? (
      <Heading {...props}>{textContent}</Heading>
    ) : (
      <Text {...props}>{textContent}</Text>
    );
  }
  
  if (isEditing) {
    return (
      <Box position="relative" my={2}>
        <FormControl>
          {as === Heading ? (
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              size="lg"
              fontWeight="bold"
              variant="flushed"
              {...props}
            />
          ) : (
            <Textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              variant="flushed"
              {...props}
            />
          )}
        </FormControl>
        <Flex mt={2}>
          <Button size="sm" leftIcon={<FaCheck />} colorScheme="green" onClick={handleSave} mr={2}>Save</Button>
          <Button size="sm" leftIcon={<FaTimes />} onClick={handleCancel}>Cancel</Button>
        </Flex>
      </Box>
    );
  }
  
  return (
    <Box position="relative" display="inline-block">
      {as === Heading ? (
        <Heading {...props}>{textContent}</Heading>
      ) : (
        <Text {...props}>{textContent}</Text>
      )}
      <Icon
        as={FaEdit}
        position="absolute"
        top={0}
        right={-8}
        cursor="pointer"
        color="blue.500"
        onClick={handleEdit}
      />
    </Box>
  );
};

export const EditableImage = ({ section, contentKey, ...props }) => {
  const { content, isEditMode, updateContent, uploadImage } = useContent();
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const imageSrc = content[section]?.[contentKey] || props.src || '';
  
  const handleEdit = () => {
    setIsEditing(true);
  };
  
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setIsUploading(true);
      const result = await uploadImage(file, section);
      
      if (result.success) {
        await updateContent(section, contentKey, result.url);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleCancel = () => {
    setIsEditing(false);
  };
  
  if (!isEditMode) {
    return <Image src={imageSrc} {...props} />;
  }
  
  return (
    <Box position="relative">
      <Image src={imageSrc} {...props} opacity={isEditing ? 0.5 : 1} />
      
      {!isEditing ? (
        <Icon
          as={FaEdit}
          position="absolute"
          top={2}
          right={2}
          cursor="pointer"
          color="blue.500"
          boxSize={6}
          onClick={handleEdit}
          bg="white"
          borderRadius="full"
          p={1}
        />
      ) : (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="white"
          p={4}
          borderRadius="md"
          shadow="md"
        >
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id={`file-upload-${section}-${contentKey}`}
          />
          <Button
            as="label"
            htmlFor={`file-upload-${section}-${contentKey}`}
            colorScheme="blue"
            isLoading={isUploading}
            mb={2}
            mr={2}
            cursor="pointer"
          >
            Choose Image
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </Box>
      )}
    </Box>
  );
};